"use client";

import type {
	FestivalDetails,
	FetchedDbSet,
	ProcessedSet,
} from "@workspace/types";
import {
	formatStageNameFromString,
	getSetDisplayName,
} from "@/lib/business-logic/set-rendering";
import { matchesSearch } from "@workspace/shared-utils";

// Local review type to match the cleaner interface
interface Review {
	setId: string;
	artistId: string;
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
}

import { parseGenres, setMatchesSearch } from "@workspace/types";
import {
	calculateSetDay,
	calculateSetDuration,
	calculateSortHour,
} from "@workspace/shared-utils";
import { useCallback, useMemo } from "react";

interface BookmarkFilters {
	saved: boolean;
	rated: boolean;
	following: boolean;
	stream: boolean;
}

interface UseFestivalDataProcessingProps {
	processedSets: ProcessedSet[];
	setReviews: Review[];
	savedSchedule: string[];
	selectedDay: string;
	sortAscending: boolean;
	sharedBookmarkFilters: BookmarkFilters | null;
	searchTerm: string;
	selectedGenre: string;
	reviewFilter: string;
	dbStagesList: { id: string; name: string; position: number }[];
	selectedStage?: string;
	socialInteractions?: Record<string, any[]>;
	eventType?: "main_festival" | "pre_party" | "after_party";
}

interface UseFestivalDataProcessingReturn {
	// Data processing functions
	processDbSets: (
		dbSets: FetchedDbSet[],
		festivalDetailsData?: FestivalDetails | null,
	) => ProcessedSet[];
	groupSetsByDay: (
		sets: ProcessedSet[],
		festival?: FestivalDetails | null,
	) => string[];

	// Computed data
	setsByStage: Record<string, ProcessedSet[]>;
	groupSetsByTimeAndDay: Record<string, Record<string, ProcessedSet[]>>;
	unfilteredGroupSetsByTimeAndDay: Record<
		string,
		Record<string, ProcessedSet[]>
	>;
	filteredArtists: ProcessedSet[];
	allGenres: string[];
	hasNoGenreArtists: boolean;
	calendarData: any[];
	unfilteredCalendarData: Array<{ stage: string }>;
	userInteractionCalendarData: Array<{ stage: string }>;
	userIntentCalendarData: Array<{ stage: string }>;
	artistsData: ProcessedSet[];
	stagesData: { stageName: string; sets: ProcessedSet[] }[];
	unfilteredStagesData: { stageName: string; sets: ProcessedSet[] }[];

	// Filter functions
	applyBookmarkFilters: (
		sets: ProcessedSet[],
		filters: BookmarkFilters | null,
	) => ProcessedSet[];
	applyReviewFilter: (sets: ProcessedSet[]) => ProcessedSet[];
	applyUserInteractionFilter: (sets: ProcessedSet[]) => ProcessedSet[];
	applyUserIntentFilter: (sets: ProcessedSet[]) => ProcessedSet[];

	// Utility functions
	getTimeLabel: (hour: number) => string;
	getDayCount: (day: string) => number;
}

export function useFestivalDataProcessing({
	processedSets,
	setReviews,
	savedSchedule,
	selectedDay,
	sortAscending,
	sharedBookmarkFilters,
	searchTerm,
	selectedGenre,
	reviewFilter,
	dbStagesList,
	selectedStage = "all",
	socialInteractions,
	eventType = "main_festival",
}: UseFestivalDataProcessingProps): UseFestivalDataProcessingReturn {
	// Process database sets into ProcessedSet format
	const processDbSets = useCallback(
		(
			dbSets: FetchedDbSet[],
			festivalDetailsData?: FestivalDetails | null,
		): ProcessedSet[] => {
			return dbSets.map((set) => {
				// Check if this is a lineup-only festival (no schedule data)
				const isLineupOnly =
					festivalDetailsData?.schedule_status === "lineup_only" ||
					!set.start_time ||
					!set.end_time ||
					!set.stage;

				// Handle lineup-only festivals with placeholder data
				if (isLineupOnly) {
					const processedSet: ProcessedSet = {
						id: set.id,
						artist: set.artist
							? {
									...set.artist,
									aliases:
										(set.artist as { aliases?: { alias_name: string }[] })
											.aliases || [],
								}
							: null,
						stage: set.stage,
						start_time: set.start_time,
						end_time: set.end_time,
						youtube_url: set.youtube_url,
						soundcloud_url: set.soundcloud_url,
						apple_music_url: set.apple_music_url,
						alias_name: set.alias_name,
						is_group_alias: set.is_group_alias,
						presents_name: set.presents_name,
						friends_set: set.friends_set,
						is_collaborative: set.is_collaborative,
						display_format: set.display_format,
						is_surprise: set.is_surprise,
						placeholder_name: set.placeholder_name,
						special_guests: set.special_guests,
						set_artists: Array.isArray(set.set_artists) ? set.set_artists : [],
						event_type: (set as any).event_type || "main_festival",
						day: "all", // No specific day for lineup-only
						startHour: 0,
						durationHours: 0,
						time: "TBA", // Placeholder time as per PRD
						isLineupOnly: true,
						// Preserve program set data if it exists
						...("_program_set" in set
							? {
									_program_set: (
										set as {
											_program_set?: {
												name: string;
												program_type: string;
												registration_required: boolean;
												description: string | null;
											};
										}
									)._program_set,
								}
							: {}),
					};
					return processedSet;
				}

				// Normal processing for festivals with complete schedules
				const startTime = new Date(set.start_time);
				const endTime = new Date(set.end_time);

				// Use shared utility for consistent day calculation
				const dayOfWeek = calculateSetDay(startTime, festivalDetailsData);

				const formatTime = (date: Date) =>
					date
						.toLocaleTimeString("en-US", {
							hour: "numeric",
							minute: "2-digit",
							hour12: true,
						})
						.replace(/ AM/g, "AM")
						.replace(/ PM/g, "PM");

				// Use shared utility for consistent sort hour calculation
				const startHour = calculateSortHour(startTime, festivalDetailsData);

				// Format times for duration calculation
				const formattedStartTime = formatTime(startTime);
				const formattedEndTime = formatTime(endTime);

				// Use shared utility for consistent duration calculation (handles midnight crossing)
				const durationHours = calculateSetDuration(
					formattedStartTime,
					formattedEndTime,
					festivalDetailsData?.schedule_overnight,
				);

				const processedSet = {
					id: set.id,
					artist: set.artist
						? {
								...set.artist,
								aliases:
									(set.artist as { aliases?: { alias_name: string }[] })
										.aliases || [],
							}
						: null,
					stage: set.stage,
					start_time: set.start_time,
					end_time: set.end_time,
					youtube_url: set.youtube_url,
					soundcloud_url: set.soundcloud_url,
					apple_music_url: set.apple_music_url,
					alias_name: set.alias_name,
					is_group_alias: set.is_group_alias,
					presents_name: set.presents_name,
					friends_set: set.friends_set,
					is_collaborative: set.is_collaborative,
					display_format: set.display_format,
					is_surprise: set.is_surprise,
					placeholder_name: set.placeholder_name,
					special_guests: set.special_guests,
					set_artists: Array.isArray(set.set_artists) ? set.set_artists : [],
					event_type: (set as any).event_type || "main_festival",
					day: dayOfWeek,
					startHour,
					durationHours,
					time: `${formattedStartTime}-${formattedEndTime}`,
					isLineupOnly: festivalDetailsData?.schedule_status === "lineup_only",
					// Preserve program set data if it exists
					...("_program_set" in set
						? {
								_program_set: (
									set as {
										_program_set?: {
											name: string;
											program_type: string;
											registration_required: boolean;
											description: string | null;
										};
									}
								)._program_set,
							}
						: {}),
				};
				return processedSet;
			});
		},
		[],
	);

	// Group sets by day and return available days
	const groupSetsByDay = useCallback(
		(sets: ProcessedSet[], festival?: FestivalDetails | null) => {
			const uniqueDays = [
				...new Set(sets.map((set) => set.day)),
			].filter((day) => day && day !== "all");
			if (uniqueDays.length === 0) return [];

			// For festivals with dates, sort by chronological order
			// Use UTC methods to avoid timezone shift (date-only strings parse as UTC)
			if (festival?.start_date && festival.end_date) {
				const dayNamesToIndex = new Map<string, number>();
				const startDate = new Date(festival.start_date);
				const endDate = new Date(festival.end_date);
				for (let i = 0; i < 14; i++) {
					const date = new Date(startDate);
					date.setUTCDate(startDate.getUTCDate() + i);
					const dayName = [
						"sunday",
						"monday",
						"tuesday",
						"wednesday",
						"thursday",
						"friday",
						"saturday",
					][date.getUTCDay()];
					if (dayName && !dayNamesToIndex.has(dayName)) dayNamesToIndex.set(dayName, i);
					if (date >= endDate) break;
				}
				return uniqueDays.sort((a, b) => {
					const indexA = dayNamesToIndex.get(a) ?? 999;
					const indexB = dayNamesToIndex.get(b) ?? 999;
					return indexA - indexB;
				});
			}
			const dayOrder = {
				sunday: 0,
				monday: 1,
				tuesday: 2,
				wednesday: 3,
				thursday: 4,
				friday: 5,
				saturday: 6,
			};
			return uniqueDays.sort((a, b) => {
				if (a === "all" || a === undefined) return -1;
				if (b === "all" || b === undefined) return 1;
				return (
					(dayOrder[a as keyof typeof dayOrder] || 99) -
					(dayOrder[b as keyof typeof dayOrder] || 99)
				);
			});
		},
		[],
	);

	// Get time label for hour
	const getTimeLabel = useCallback((hour: number): string => {
		if (hour === 0) return "12:00AM";
		if (hour === 12) return "12:00PM";
		if (hour < 12) return `${hour}:00AM`;
		return `${hour - 12}:00PM`;
	}, []);

	// Apply bookmark filters
	const applyBookmarkFilters = useCallback(
		(sets: ProcessedSet[], filters: BookmarkFilters | null) => {
			if (!filters) return sets;
			const { saved, rated, following, stream } = filters;

			// If no filters active, show all
			if (!saved && !rated && !following && !stream) return sets;

			const filteredSets = sets.filter((set) => {
				const review = setReviews.find((r) => r.setId === set.id);

				// Updated saved logic: want to watch + must watch + rated (star/heart/comment) + saw stream
				const matchesSaved =
					!saved ||
					review?.wantToSee ||
					review?.mustSee ||
					(review?.rating || 0) > 0 ||
					review?.loved ||
					(review?.content && review.content.trim().length > 0) ||
					review?.sawStream;
				const matchesRated =
					!rated ||
					(review?.rating || 0) > 0 ||
					review?.loved ||
					(review?.content && review.content.trim().length > 0);
				const matchesStream = !stream || !!set.youtube_url;
				const interactions = socialInteractions?.[set.id];
				const matchesFollowing =
					!following || (interactions && interactions.length > 0);

				return (
					matchesSaved && matchesRated && matchesFollowing && matchesStream
				);
			});

			return filteredSets;
		},
		[setReviews, socialInteractions],
	);

	// Apply user interaction filter (independent of bookmark filters)
	const applyUserInteractionFilter = useCallback(
		(sets: ProcessedSet[]) => {
			return sets.filter((set) => {
				const review = setReviews.find((r) => r.setId === set.id);
				const isInSchedule = savedSchedule.includes(set.id);
				const interactions = socialInteractions?.[set.id];

				// Check if user has interacted with this set in any way
				const hasInteraction =
					// Has any review data
					review?.wantToSee ||
					review?.mustSee ||
					(review?.rating || 0) > 0 ||
					review?.loved ||
					(review?.content && review.content.trim().length > 0) ||
					review?.sawLive ||
					review?.sawStream ||
					// Is in saved schedule
					isInSchedule ||
					// Has social interactions (following artists)
					(interactions && interactions.length > 0);

				return hasInteraction;
			});
		},
		[setReviews, savedSchedule, socialInteractions],
	);

	// Apply user intent filter for stage filtering (excludes stream and viewing history)
	// This focuses on explicit user intent: saved, rated, loved, or following
	const applyUserIntentFilter = useCallback(
		(sets: ProcessedSet[]) => {
			return sets.filter((set) => {
				const review = setReviews.find((r) => r.setId === set.id);
				const isInSchedule = savedSchedule.includes(set.id);
				const interactions = socialInteractions?.[set.id];

				// Check only for explicit user intent (excludes stream filter and viewing history)
				const hasUserIntent =
					// Saved for future (want to see / must see)
					review?.wantToSee ||
					review?.mustSee ||
					// Rated or loved
					(review?.rating || 0) > 0 ||
					review?.loved ||
					// Written review content
					(review?.content && review.content.trim().length > 0) ||
					// Is in saved schedule
					isInSchedule ||
					// Following the artist(s)
					(interactions && interactions.length > 0);

				// Explicitly exclude sawLive and sawStream as these are past actions, not future intent
				// Also exclude stream filter as it's based on content availability, not user preference

				return hasUserIntent;
			});
		},
		[setReviews, savedSchedule, socialInteractions],
	);

	// Apply review filter
	const applyReviewFilter = useCallback(
		(sets: ProcessedSet[]) =>
			sets.filter((set) => {
				const review = setReviews.find((r) => r.setId === set.id);
				const isInSchedule = savedSchedule.includes(set.id);
				switch (reviewFilter) {
					case "want":
						return (
							review?.wantToSee &&
							!review?.sawLive &&
							!review?.sawStream &&
							!review?.mustSee
						);
					case "must":
						return review?.mustSee;
					case "saw-live":
						return review?.sawLive;
					case "saw-stream":
						return review?.sawStream;
					case "rated":
						return review && review.rating > 0;
					case "following": {
						const interactions = socialInteractions?.[set.id];
						return interactions && interactions.length > 0;
					}
					case "loved":
						return review?.loved;
					case "all":
						// Show any set that has a review or is in the schedule
						// "All" means show everything the user has interacted with
						return isInSchedule || review !== undefined;
					default:
						return false;
				}
			}),
		[setReviews, savedSchedule, reviewFilter, socialInteractions],
	);

	// Filter sets by event type
	const eventFilteredSets = useMemo(() => {
		// For integrated event types (main_festival, pre_party, after_party),
		// filter by the event_type field in the set data
		return processedSets.filter((set) => {
			// Check if set has event_type field (from database)
			const setEventType = (set as any).event_type || "main_festival";
			return setEventType === eventType;
		});
	}, [processedSets, eventType]);

	// Group sets by stage
	const setsByStage = useMemo(() => {
		const grouped: Record<string, ProcessedSet[]> = {};
		if (!eventFilteredSets || !selectedDay) return grouped;

		// Add sets to their respective stages
		for (const set of eventFilteredSets.filter((set) => set.day === selectedDay)) {
			const stageName = set.stage?.name || "Stage: TBA";
			if (!grouped[stageName]) grouped[stageName] = [];
			grouped[stageName].push(set);
		}

		// Sort sets within each stage
		for (const sets of Object.values(grouped)) {
			sets.sort((a, b) => {
				// Handle null/undefined start_time for lineup-only festivals
				if (!a.start_time && !b.start_time) return 0;
				if (!a.start_time) return 1;
				if (!b.start_time) return -1;
				const timeA = new Date(a.start_time).getTime();
				const timeB = new Date(b.start_time).getTime();
				return sortAscending ? timeA - timeB : timeB - timeA;
			});
		}
		return grouped;
	}, [eventFilteredSets, selectedDay, sortAscending]);

	// Track which stages actually have sets (before filtering)
	// This ensures stages remain visible even when bookmark filters make them empty
	const stagesWithSets = useMemo(() => {
		return new Set(Object.keys(setsByStage));
	}, [setsByStage]);

	// Helper function to group sets by time and day
	const groupSetsByTimeAndDayHelper = useCallback(
		(sets: ProcessedSet[]) => {
			const grouped: Record<string, Record<string, ProcessedSet[]>> = {};

			for (const set of sets) {
				const startTime = new Date(set.start_time);
				const hour = startTime.getHours();
				const timeLabel = getTimeLabel(hour);

				if (!grouped[set.day]) grouped[set.day] = {};
				if (!grouped[set.day]?.[timeLabel]) grouped[set.day]![timeLabel] = [];
				grouped[set.day]?.[timeLabel]?.push(set);
			}
			for (const day of Object.keys(grouped)) {
				const dayGroup = grouped[day];
				if (dayGroup) {
					for (const timeLabel of Object.keys(dayGroup)) {
						const timeGroup = dayGroup[timeLabel];
						if (timeGroup)
							timeGroup.sort((a, b) => {
								// Handle null/undefined start_time for lineup-only festivals
								if (!a.start_time && !b.start_time) return 0;
								if (!a.start_time) return 1;
								if (!b.start_time) return -1;
								const timeA = new Date(a.start_time).getTime();
								const timeB = new Date(b.start_time).getTime();
								if (timeA !== timeB) return timeA - timeB;
								return formatStageNameFromString(a.stage?.name).localeCompare(
									formatStageNameFromString(b.stage?.name),
								);
							});
					}
				}
			}
			return grouped;
		},
		[getTimeLabel],
	);

	// Group sets by time and day (filtered)
	const groupSetsByTimeAndDay = useMemo(() => {
		const filteredSets = applyBookmarkFilters(
			eventFilteredSets,
			sharedBookmarkFilters,
		);
		return groupSetsByTimeAndDayHelper(filteredSets);
	}, [
		eventFilteredSets,
		sharedBookmarkFilters,
		applyBookmarkFilters,
		groupSetsByTimeAndDayHelper,
	]);

	// Group sets by time and day (unfiltered)
	const unfilteredGroupSetsByTimeAndDay = useMemo(() => {
		return groupSetsByTimeAndDayHelper(eventFilteredSets);
	}, [eventFilteredSets, groupSetsByTimeAndDayHelper]);

	// Filter artists
	const filteredArtists = useMemo(
		() =>
			eventFilteredSets
				.filter(
					(set) =>
						!searchTerm || setMatchesSearch(set, searchTerm, matchesSearch),
				)
				.filter((set) => {
					if (selectedGenre === "all") return true;
					if (set.is_collaborative && set.set_artists)
						return set.set_artists.some((setArtist) =>
							selectedGenre === "no-genre"
								? !setArtist.artist.spotify_genres ||
									setArtist.artist.spotify_genres.length === 0
								: parseGenres(
										setArtist.artist.spotify_genres || null,
										setArtist.artist.manual_genres || null,
									).includes(selectedGenre),
						);
					return selectedGenre === "no-genre"
						? !set.artist?.spotify_genres ||
								set.artist.spotify_genres.length === 0
						: parseGenres(
								set.artist?.spotify_genres || null,
								set.artist?.manual_genres || null,
							).includes(selectedGenre);
				}),
		[eventFilteredSets, searchTerm, selectedGenre],
	);

	// Get all genres
	const allGenres = useMemo(() => {
		const genreSet = new Set<string>();
		for (const set of eventFilteredSets) {
			if (set.is_collaborative && set.set_artists) {
				for (const setArtist of set.set_artists) {
					if (setArtist.artist) {
						for (const genre of parseGenres(
							setArtist.artist.spotify_genres || null,
							setArtist.artist.manual_genres || null,
						)) {
							genreSet.add(genre);
						}
					}
				}
			} else if (set.artist) {
				for (const genre of parseGenres(
					set.artist.spotify_genres || null,
					set.artist.manual_genres || null,
				)) {
					genreSet.add(genre);
				}
			}
		}
		// Sort genres alphabetically for consistent display
		return Array.from(genreSet).sort((a, b) =>
			a.toLowerCase().localeCompare(b.toLowerCase()),
		);
	}, [eventFilteredSets]);

	// Check if there are artists without genres
	const hasNoGenreArtists = useMemo(
		() =>
			eventFilteredSets.some((set) =>
				set.is_collaborative && set.set_artists
					? set.set_artists.some(
							(setArtist) =>
								!setArtist.artist.spotify_genres ||
								setArtist.artist.spotify_genres.length === 0,
						)
					: !set.artist?.spotify_genres ||
						set.artist.spotify_genres.length === 0,
			),
		[eventFilteredSets],
	);

	// Calculate unfiltered calendar data (for determining which stages to show)
	const unfilteredCalendarData = useMemo(() => {
		return eventFilteredSets
			.filter((set) => set.day === selectedDay)
			.map((set) => ({
				stage: set.stage?.name || "Stage: TBA",
			}));
	}, [eventFilteredSets, selectedDay]);

	// Calculate user interaction calendar data (shows stages with sets user has interacted with)
	const userInteractionCalendarData = useMemo(() => {
		const interactionFilteredSets = applyUserInteractionFilter(
			eventFilteredSets.filter((set) => set.day === selectedDay),
		);

		return interactionFilteredSets.map((set) => ({
			stage: set.stage?.name || "Stage: TBA",
		}));
	}, [eventFilteredSets, selectedDay, applyUserInteractionFilter]);

	// Calculate user intent calendar data (shows stages with sets based on user intent, excluding stream)
	const userIntentCalendarData = useMemo(() => {
		const intentFilteredSets = applyUserIntentFilter(
			eventFilteredSets.filter((set) => set.day === selectedDay),
		);

		return intentFilteredSets.map((set) => ({
			stage: set.stage?.name || "Stage: TBA",
		}));
	}, [eventFilteredSets, selectedDay, applyUserIntentFilter]);

	// Calculate calendar data (includes all days for multi-day export support)
	const calendarData = useMemo(() => {
		const filteredSets = applyBookmarkFilters(eventFilteredSets, sharedBookmarkFilters);

		return filteredSets.map((set) => {
			const review = setReviews.find((r) => r.setId === set.id);
			return {
				id: set.id,
				artist: getSetDisplayName(set),
				stage: set.stage?.name || "Stage: TBA",
				time: set.time,
				startHour: set.startHour,
				duration: set.durationHours,
				day: set.day,
				imageUrl: set.artist?.image_url || null,
				youtube_url: set.youtube_url,
				soundcloud_url: set.soundcloud_url,
				apple_music_url: set.apple_music_url,
				status: {
					isInSchedule: savedSchedule.includes(set.id),
					sawLive: review?.sawLive || false,
					sawStream: review?.sawStream || false,
					mustSee: review?.mustSee || false,
				},
			};
		});
	}, [
		eventFilteredSets,
		sharedBookmarkFilters,
		applyBookmarkFilters,
		setReviews,
		savedSchedule,
	]);

	// Artists data
	const artistsData = useMemo(
		() => applyBookmarkFilters(filteredArtists, sharedBookmarkFilters),
		[filteredArtists, sharedBookmarkFilters, applyBookmarkFilters],
	);

	// Stages data
	const stagesData = useMemo(() => {
		// Only show stages that actually have sets for this festival
		const stagesToShow = Array.from(stagesWithSets).filter(
			(stageName) =>
				selectedStage === "all" ||
				dbStagesList.find((s) => s.name === stageName)?.id === selectedStage,
		);

		return stagesToShow
			.map((stageName) => ({
				stageName,
				sets: applyBookmarkFilters(
					setsByStage[stageName] || [],
					sharedBookmarkFilters,
				),
			}))
			.sort((a, b) => {
				const stageAPos =
					dbStagesList.find((s) => s.name === a.stageName)?.position || 999;
				const stageBPos =
					dbStagesList.find((s) => s.name === b.stageName)?.position || 999;

				return stageAPos - stageBPos;
			});
	}, [
		setsByStage,
		stagesWithSets,
		selectedStage,
		dbStagesList,
		sharedBookmarkFilters,
		applyBookmarkFilters,
	]);

	// Unfiltered stages data (for detecting if empty state is due to filters)
	const unfilteredStagesData = useMemo(() => {
		// Only show stages that actually have sets for this festival
		const stagesToShow = Array.from(stagesWithSets).filter(
			(stageName) =>
				selectedStage === "all" ||
				dbStagesList.find((s) => s.name === stageName)?.id === selectedStage,
		);

		return stagesToShow
			.map((stageName) => ({
				stageName,
				sets: setsByStage[stageName] || [],
			}))
			.sort((a, b) => {
				const stageAPos =
					dbStagesList.find((s) => s.name === a.stageName)?.position || 999;
				const stageBPos =
					dbStagesList.find((s) => s.name === b.stageName)?.position || 999;

				return stageAPos - stageBPos;
			});
	}, [setsByStage, stagesWithSets, selectedStage, dbStagesList]);

	// Get day count for review filter
	const getDayCount = useCallback(
		(day: string): number => {
			const reviewFilteredSets = applyReviewFilter(eventFilteredSets);
			const counts: Record<string, number> = {};
			const availableDays = [...new Set(eventFilteredSets.map((s) => s.day))];

			for (const availableDay of availableDays) {
				counts[availableDay] = 0;
			}
			for (const set of reviewFilteredSets) {
				if (set.day && counts[set.day] !== undefined)
					counts[set.day] = (counts[set.day] || 0) + 1;
			}
			counts.total = reviewFilteredSets.length;
			return counts[day] || 0;
		},
		[eventFilteredSets, applyReviewFilter],
	);

	return {
		// Data processing functions
		processDbSets,
		groupSetsByDay,

		// Computed data
		setsByStage,
		groupSetsByTimeAndDay,
		unfilteredGroupSetsByTimeAndDay,
		filteredArtists,
		allGenres,
		hasNoGenreArtists,
		calendarData,
		unfilteredCalendarData,
		userInteractionCalendarData,
		userIntentCalendarData,
		artistsData,
		stagesData,
		unfilteredStagesData,

		// Filter functions
		applyBookmarkFilters,
		applyReviewFilter,
		applyUserInteractionFilter,
		applyUserIntentFilter,

		// Utility functions
		getTimeLabel,
		getDayCount,
	};
}
